import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

interface BoardGridContextMenuProps {
    x: number;
    y: number;
    isOpen: boolean;
    onClose: () => void;
    onDeleteChart?: () => void;
    onAddChart: () => void;
    onRefresh: () => void;
    targetChartId?: string;
}

export const BoardGridContextMenu: React.FC<BoardGridContextMenuProps> = ({
    x,
    y,
    isOpen,
    onClose,
    onDeleteChart,
    onAddChart,
    onRefresh,
    targetChartId
}) => {
    const { t } = useTranslation('common');
    const menuRef = useRef<HTMLDivElement>(null);
    const [adjustedPos, setAdjustedPos] = useState({ x, y });

    // Handle clicking outside
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (isOpen && menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        window.addEventListener('mousedown', handleClick);
        return () => window.removeEventListener('mousedown', handleClick);
    }, [isOpen, onClose]);

    // Handle positioning to stay within viewport
    useEffect(() => {
        if (!isOpen) return;

        const menu = menuRef.current;
        if (!menu) return;

        const rect = menu.getBoundingClientRect();
        const padding = 10;
        let finalX = x;
        let finalY = y;

        if (x + rect.width > window.innerWidth - padding) {
            finalX = x - rect.width;
        }
        if (y + rect.height > window.innerHeight - padding) {
            finalY = y - rect.height;
        }

        setAdjustedPos({ x: finalX, y: finalY });
    }, [isOpen, x, y]);

    if (!isOpen) return null;

    const items = [
        ...(targetChartId ? [{
            label: t('actions.delete') || 'Delete',
            onClick: onDeleteChart,
            icon: 'https://cdn.dashbal.com/assets/icon_fd779e52.svg',
            variant: 'danger'
        }] : []),
        {
            label: t('charts.addCharts') || 'Add Chart',
            onClick: onAddChart,
            icon: 'https://cdn.dashbal.com/assets/icon_47ddeb53.svg'
        },
        {
            label: t('actions.refresh') || 'Refresh',
            onClick: onRefresh,
            icon: 'https://cdn.dashbal.com/assets/icon_741f8e9e.svg'
        }
    ];

    return createPortal(
        <AnimatePresence>
            <motion.div
                ref={menuRef}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.1 }}
                className="fixed z-[9999] bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-2xl py-1.5 min-w-[180px] overflow-hidden backdrop-blur-xl bg-white/90 dark:bg-neutral-900/90"
                style={{ left: adjustedPos.x, top: adjustedPos.y }}
            >
                {items.map((item, idx) => (
                    <button
                        key={idx}
                        onClick={(e) => {
                            e.stopPropagation();
                            item.onClick?.();
                            onClose();
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors text-left
                            ${item.variant === 'danger' 
                                ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20' 
                                : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                            }`}
                    >
                        <span 
                            className="w-4 h-4 shrink-0 opacity-60" 
                            style={{ 
                                maskImage: `url(${item.icon})`, 
                                WebkitMaskImage: `url(${item.icon})`, 
                                maskRepeat: 'no-repeat', 
                                WebkitMaskRepeat: 'no-repeat', 
                                maskPosition: 'center', 
                                WebkitMaskPosition: 'center', 
                                maskSize: 'contain', 
                                WebkitMaskSize: 'contain', 
                                backgroundColor: 'currentColor', 
                                display: 'inline-block' 
                            }} 
                        />
                        <span className="font-medium">{item.label}</span>
                    </button>
                ))}
            </motion.div>
        </AnimatePresence>,
        document.body
    );
};
